import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PILLARS, PillarBadge, GoldBadge } from "@/constants/pillars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Trash2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

function Specialists() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(null);
  useEffect(() => { api.get("/specialists", { params: filter ? { pillar: filter } : {} }).then((r) => setItems(r.data)); }, [filter]);
  const removeUser = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare l'utente ${s.name} e tutti i suoi contenuti?`)) return;
    try { await api.delete(`/admin/users/${s.id}`); setItems((it) => it.filter((x) => x.id !== s.id)); toast.success("Utente eliminato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex gap-2 overflow-x-auto mb-4 pb-1">
        <button onClick={() => setFilter(null)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${!filter ? "bg-black text-white" : "bg-neutral-100"}`}>Tutti</button>
        {PILLARS.map((p) => <button key={p.id} onClick={() => setFilter(p.id)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${filter === p.id ? "bg-black text-white" : "bg-neutral-100"}`}>{p.name}</button>)}
      </div>
      {items.length === 0 ? <p className="text-center text-neutral-400 py-8 text-sm">Nessuno specialista.</p> :
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`specialist-${s.id}`} className="flex items-center gap-3 border border-neutral-200 rounded-2xl p-3">
              <Avatar className="h-12 w-12"><AvatarImage src={fileUrl(s.avatar_url)} /><AvatarFallback className="bg-black text-white">{s.name[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5"><span className="font-semibold text-sm">{s.name}</span>{s.is_gold && <GoldBadge />}</div>
                <p className="text-xs text-neutral-500">{s.title || s.role}</p>
                {s.pillar && <PillarBadge id={s.pillar} className="mt-1" />}
              </div>
              {user?.role === "admin" && (
                <button data-testid={`delete-user-${s.id}`} onClick={(e) => removeUser(e, s)} title="Elimina utente" className="text-neutral-300 hover:text-red-500 p-2"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}

function Gold() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [live, setLive] = useState(false);
  const lastRef = useRef(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  useEffect(() => { lastRef.current = msgs.length ? msgs[msgs.length - 1].created_at : null; }, [msgs]);
  useEffect(() => {
    if (!live) return;
    const poll = async () => {
      try {
        const { data: m } = await api.get("/community/messages", { params: lastRef.current ? { after: lastRef.current } : {} });
        if (m.length) setMsgs((prev) => { const ids = new Set(prev.map((x) => x.id)); return [...prev, ...m.filter((x) => !ids.has(x.id))]; });
      } catch {}
    };
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [live]);
  const [text, setText] = useState("");
  const [cards, setCards] = useState([]);
  const [selCard, setSelCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const { data } = await api.get("/gold/status"); setStatus(data);
    try { const { data: c } = await api.get("/payments/methods"); setCards(c); if (c.length) setSelCard(c[0].id); } catch {}
    if (data.is_gold || user.role === "admin") { try { const { data: m } = await api.get("/community/messages"); setMsgs(m); } catch {} }
    setLive(data.is_gold || user.role === "admin");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const subscribe = async () => {
    try { const { data } = await api.post("/gold/subscribe", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const subscribeSaved = async () => {
    setBusy(true);
    try { await api.post("/gold/subscribe-saved", { payment_method_id: selCard }); toast.success("Partner Oro attivo!"); await refreshUser(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!text.trim()) return;
    const { data } = await api.post("/community/messages", { text }); setMsgs((prev) => prev.some((x) => x.id === data.id) ? prev : [...prev, data]); setText("");
  };
  const isGold = status?.is_gold || user.role === "admin";
  return (
    <div>
      <div className="rounded-2xl bg-[#0a0a0a] text-white p-6 relative overflow-hidden">
        <div className="absolute inset-0 enb-grain opacity-40" />
        <div className="relative">
          <Crown className="text-[#d4af37]" size={28} />
          <h2 className="font-display text-xl font-bold mt-3">Partner Oro</h2>
          <p className="text-sm text-neutral-300 mt-1">Community riservata, badge Oro e visibilità prioritaria.</p>
          <p className="font-display text-2xl font-extrabold mt-3">€{status?.price || 30}<span className="text-sm font-normal text-neutral-400">/mese</span></p>
          {!isGold && (
            <div className="mt-4 flex flex-col gap-2">
              {cards.length > 1 && (
                <Select value={selCard} onValueChange={setSelCard}>
                  <SelectTrigger data-testid="gold-card-select" className="h-11 rounded-full bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cards.map((c) => <SelectItem key={c.id} value={c.id}><span className="capitalize">{c.brand}</span> •••• {c.last4}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {cards.length > 0 && (
                <Button data-testid="gold-pay-saved-btn" onClick={subscribeSaved} disabled={busy}
                  className="h-11 rounded-full bg-[#d4af37] text-black hover:bg-[#c9a431] font-semibold">
                  {busy ? "Attivo…" : `Attiva con carta · •••• ${(cards.find((c) => c.id === selCard) || cards[0]).last4}`}
                </Button>
              )}
              <Button data-testid="gold-subscribe-btn" onClick={subscribe} variant="outline"
                className={cards.length > 0 ? "h-11 rounded-full border-white/30 bg-transparent text-white hover:bg-white/10" : "h-11 rounded-full bg-[#d4af37] text-black hover:bg-[#c9a431] font-semibold border-0"}>
                {cards.length > 0 ? "oppure abbonati con checkout" : "Diventa Partner Oro"}
              </Button>
            </div>
          )}
          {isGold && <p className="mt-3 text-[#d4af37] font-semibold text-sm">Sei un Partner Oro ✓</p>}
        </div>
      </div>
      {isGold && (
        <div className="mt-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2">Community Oro <span data-testid="community-live" className="text-[10px] font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">● live</span></h3>
          <div data-testid="community-messages" className="space-y-2 max-h-80 overflow-y-auto mb-3">
            {msgs.map((m) => (
              <div key={m.id} data-testid={`community-msg-${m.id}`} className="flex gap-2 text-sm"><span className="font-semibold">{m.author?.name}:</span><span className="text-neutral-700">{m.text}</span></div>
            ))}
            {msgs.length === 0 && <p className="text-xs text-neutral-400">Nessun messaggio ancora.</p>}
            <div ref={endRef} />
          </div>
          <div className="flex gap-2">
            <Input data-testid="community-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi alla community…" className="h-10" />
            <Button data-testid="community-send" onClick={send} className="h-10 rounded-full bg-black text-white px-4">Invia</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NetworkPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState("specialisti");
  return (
    <div className="p-4">
      <h1 className="font-display text-2xl font-bold mb-4">{t("network")}</h1>
      <div className="flex gap-2 mb-5 bg-neutral-100 rounded-full p-1">
        {[["specialisti", t("specialists")], ["oro", t("gold_partner")]].map(([id, label]) => (
          <button key={id} data-testid={`network-tab-${id}`} onClick={() => setTab(id)} className={`flex-1 h-9 rounded-full text-xs font-semibold transition-colors ${tab === id ? "bg-black text-white" : "text-neutral-600"}`}>{label}</button>
        ))}
      </div>
      {tab === "specialisti" && <Specialists />}
      {tab === "oro" && <Gold />}
    </div>
  );
}
